<script setup lang="ts">
import type { RadioGroupItemProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { Circle } from 'lucide';
import { RadioGroupIndicator, RadioGroupItem, useForwardProps } from 'reka-ui';
import SphIcon from '@/components/base/icon/';
import { cn } from '@/utils/shadcn/lib/utils.ts';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: true,
});

const props = defineProps<RadioGroupItemProps & {
    class?: HTMLAttributes['class']
    id: string
    value: any
}>();

const delegatedProps = reactiveOmit(props, 'class');

const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <RadioGroupItem
        v-bind="forwardedProps"
        data-slot="radio-group-item"
        :class="
            cn(
                'border-input-dark text-accent data-[state=checked]:border-accent-light! aspect-square size-5 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none',
                props.class,
            )
        "
    >
        <transition name="radio-indicator">
            <RadioGroupIndicator
                data-slot="radio-group-indicator"
                class="relative flex items-center justify-center"
            >
                <SphIcon
                    :icon="Circle"
                    class="text-accent **:[circle]:fill-accent absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-2.5!"
                />
            </RadioGroupIndicator>
        </transition>
    </RadioGroupItem>
</template>

<style scoped>
.radio-indicator-enter-active,
.radio-indicator-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s var(--ease-out-cubic);
}
.radio-indicator-enter-from,
.radio-indicator-leave-to {
    opacity: 0;
    transform: scale(0.5);
}
.radio-indicator-enter-to,
.radio-indicator-leave-from {
    opacity: 1;
    transform: scale(1);
}
</style>
